import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { supabase } from '../services/supabase';
import { Platform } from '../utils/platform';
import { storage } from '../utils/storage';

// UUID generation function that works in all environments
const generateUUID = (): string => {
  // Use crypto.randomUUID if available (Node.js environment)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Use the Web Crypto API for browsers
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, c =>
      (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
  }
  
  // Fallback to a simple timestamp-based UUID for React Native
  const timestamp = new Date().getTime();
  const randomNum = Math.random().toString(36).substring(2, 15);
  return `${timestamp}-${randomNum}`;
};

// Get the appropriate storage implementation
const getStorage = () => {
  if (Platform.isNative && (global as any).__FLOWS_STORAGE__) {
    return (global as any).__FLOWS_STORAGE__;
  }
  return storage;
};

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  isDefault?: boolean;
  rate?: number;
  user_id?: string;
  updated_at?: string;
}

interface CurrenciesState {
  currencies: Currency[];
  loading: boolean;
  error: string | null;
  exchangeRates: Record<string, number> | null;
  lastRatesUpdate: number | null;
  fetchCurrencies: (userId: string) => Promise<void>;
  addCurrency: (userId: string, currency: Omit<Currency, 'id'>) => Promise<void>;
  updateCurrency: (userId: string, currency: Currency) => Promise<void>;
  deleteCurrency: (userId: string, currencyId: string) => Promise<void>;
  setDefaultCurrency: (userId: string, currencyId: string) => Promise<void>;
  initializeDefaultCurrencies: (userId: string) => Promise<void>;
  fetchExchangeRates: () => Promise<Record<string, number> | null>;
  forceUpdateExchangeRates: () => Promise<Record<string, number> | null>;
  convertAmount: (amount: number, fromCurrency: string, toCurrency: string) => number;
}

const defaultCurrencies = [
  {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    isDefault: true,
    rate: 1,
  },
  {
    code: 'TRY',
    name: 'Turkish Lira',
    symbol: '₺',
    rate: 1,
  },
  {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    rate: 1,
  },
  {
    code: 'GBP',
    name: 'British Pound',
    symbol: '£',
    rate: 1,
  },
  {
    code: 'SAR',
    name: 'Saudi Riyal',
    symbol: 'ر.س',
    rate: 1,
  },
  {
    code: 'AED',
    name: 'UAE Dirham',
    symbol: 'د.إ',
    rate: 1,
  },
  {
    code: 'KWD',
    name: 'Kuwaiti Dinar',
    symbol: 'د.ك',
    rate: 1,
  },
  {
    code: 'QAR',
    name: 'Qatari Riyal',
    symbol: 'ر.ق',
    rate: 1,
  },
  {
    code: 'BHD',
    name: 'Bahraini Dinar',
    symbol: 'د.ب',
    rate: 1,
  },
  {
    code: 'OMR',
    name: 'Omani Rial',
    symbol: 'ر.ع',
    rate: 1,
  }
];

export const useCurrenciesStore = create<CurrenciesState>()(
  persist(
    (set, get) => ({
      currencies: [],
      loading: false,
      error: null,
      exchangeRates: null,
      lastRatesUpdate: null,

      fetchCurrencies: async (userId: string) => {
        if (!userId) {
          console.error('No userId provided for fetchCurrencies');
          set({ error: 'No userId provided' });
          return;
        }

        try {
          set({ loading: true, error: null });
          const { data: dbCurrencies, error } = await supabase
            .from('currencies')
            .select('*')
            .eq('user_id', userId);

          if (error) throw error;
          
          if (!dbCurrencies || dbCurrencies.length === 0) {
            await get().initializeDefaultCurrencies(userId);
            return;
          }

          // Convert snake_case to camelCase for application use
          const currencies = dbCurrencies.map(c => ({
            id: c.id,
            code: c.code,
            name: c.name,
            symbol: c.symbol,
            isDefault: c.is_default,
            rate: c.rate,
            user_id: c.user_id,
            updated_at: c.updated_at
          }));

          // Ensure only one currency is default
          const defaultCurrencies = currencies.filter((c: Currency) => c.isDefault);
          if (defaultCurrencies.length > 1) {
            // Keep the first default currency and unset others
            const [_firstDefault, ...othersToUnset] = defaultCurrencies;
            for (const currency of othersToUnset) {
              await supabase
                .from('currencies')
                .update({ is_default: false, updated_at: new Date().toISOString() })
                .eq('id', currency.id)
                .eq('user_id', userId);
              currency.isDefault = false;
            }
          } else if (defaultCurrencies.length === 0 && currencies.length > 0) {
            // If no default currency, set the first one as default
            const firstCurrency = currencies[0];
            await supabase
              .from('currencies')
              .update({ is_default: true, updated_at: new Date().toISOString() })
              .eq('id', firstCurrency.id)
              .eq('user_id', userId);
            firstCurrency.isDefault = true;
          }
          
          set({ currencies, loading: false, error: null });
        } catch (error) {
          console.error('Error fetching currencies:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to load currencies',
            loading: false 
          });
        }
      },

      fetchExchangeRates: async () => {
        try {
          console.log('[useCurrenciesStore] Fetching exchange rates');
          // Import exchange rate service dynamically to avoid circular dependencies
          const { exchangeRateService } = await import('../services/exchangeRates');
          
          let rates;
          
          // Load existing rates from the database, don't call the API
          rates = await exchangeRateService.getExistingRates();
          
          if (!rates) {
            console.log('[useCurrenciesStore] No existing rates found, fetching from API');
            // Direct API call to bypass any caching
            rates = await exchangeRateService.fetchRatesDirectlyFromAPI();
          }
          
          if (!rates) {
            console.warn('[useCurrenciesStore] No exchange rates returned from service');
            return null;
          }
          
          console.log(`[useCurrenciesStore] Received exchange rates for ${Object.keys(rates.rates).length} currencies`);
          
          // Update store with the fetched rates
          set({
            exchangeRates: rates.rates,
            lastRatesUpdate: rates.timestamp
          });

          // Get the current state to update user currencies with new rates
          const { currencies } = get();
          
          // Only update currencies in memory if we have both currencies and rates
          if (currencies && currencies.length > 0 && rates.rates) {
            console.log('[useCurrenciesStore] Updating user currencies with latest exchange rates');
            
            // Update all currencies with their latest rates
            const updatedCurrencies = currencies.map(currency => {
              // Only update if we have a rate for this currency
              if (rates.rates[currency.code]) {
                return {
                  ...currency,
                  rate: rates.rates[currency.code],
                  updated_at: new Date().toISOString()
                };
              }
              return currency;
            });
            
            // Update currencies in store
            set({ currencies: updatedCurrencies });
            
            // If we have a Supabase connection, update currencies in the database as well
            try {
              // Collect all currencies that need to be updated in the database
              const currenciesToUpdate = updatedCurrencies.filter(currency => 
                rates.rates[currency.code] && currency.user_id
              );
              
              if (currenciesToUpdate.length > 0) {
                console.log(`[useCurrenciesStore] Updating ${currenciesToUpdate.length} currencies in database with latest rates`);
                
                // Update each currency in the database with its new rate
                await Promise.all(currenciesToUpdate.map(async currency => {
                  // Skip if no user_id
                  if (!currency.user_id) return;
                  
                  try {
                    const updateData = {
                      rate: rates.rates[currency.code],
                      updated_at: new Date().toISOString()
                    };
                    
                    const { error } = await supabase
                      .from('currencies')
                      .update(updateData)
                      .eq('id', currency.id)
                      .eq('user_id', currency.user_id);
                      
                    if (error) {
                      console.error(`[useCurrenciesStore] Error updating currency ${currency.code} in database:`, error);
                    }
                  } catch (updateError) {
                    console.error(`[useCurrenciesStore] Error updating currency ${currency.code} in database:`, updateError);
                  }
                }));
              }
            } catch (updateError) {
              console.error('[useCurrenciesStore] Error updating currencies in database:', updateError);
              // Don't throw here - we already have the rates in memory which is the main goal
            }
          }
          
          return rates.rates;
        } catch (error) {
          console.error('[useCurrenciesStore] Error fetching exchange rates:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch exchange rates'
          });
          return null;
        }
      },

      /**
       * Force update of exchange rates directly from API, bypassing any caching
       * @returns Updated exchange rates or null if failed
       */
      forceUpdateExchangeRates: async () => {
        try {
          console.log('[useCurrenciesStore] Forcing update of exchange rates directly from API');
          
          // Import exchange rate service dynamically to avoid circular dependencies
          const { exchangeRateService } = await import('../services/exchangeRates');
          
          // Directly fetch from API, bypassing any caching
          const rates = await exchangeRateService.fetchRatesDirectlyFromAPI();
          
          if (!rates) {
            console.warn('[useCurrenciesStore] No exchange rates returned from direct API fetch');
            return null;
          }
          
          console.log(`[useCurrenciesStore] Received fresh exchange rates for ${Object.keys(rates.rates).length} currencies from API`);
          
          // Update store with the fetched rates
          set({
            exchangeRates: rates.rates,
            lastRatesUpdate: rates.timestamp
          });
          
          // Get the current state to update user currencies with new rates
          const { currencies } = get();
          
          // Only update currencies in memory if we have both currencies and rates
          if (currencies && currencies.length > 0 && rates.rates) {
            console.log('[useCurrenciesStore] Updating user currencies with fresh exchange rates');
            
            // Update all currencies with their latest rates
            const updatedCurrencies = currencies.map(currency => {
              // Only update if we have a rate for this currency
              if (rates.rates[currency.code]) {
                return {
                  ...currency,
                  rate: rates.rates[currency.code],
                  updated_at: new Date().toISOString()
                };
              }
              return currency;
            });
            
            // Update currencies in store
            set({ currencies: updatedCurrencies });
            
            // If we have a Supabase connection, update currencies in the database as well
            try {
              // Collect all currencies that need to be updated in the database
              const currenciesToUpdate = updatedCurrencies.filter(currency => 
                rates.rates[currency.code] && currency.user_id
              );
              
              if (currenciesToUpdate.length > 0) {
                console.log(`[useCurrenciesStore] Updating ${currenciesToUpdate.length} currencies in database with fresh rates`);
                
                // Update each currency in the database with its new rate
                await Promise.all(currenciesToUpdate.map(async currency => {
                  // Skip if no user_id
                  if (!currency.user_id) return;
                  
                  try {
                    const updateData = {
                      rate: rates.rates[currency.code],
                      updated_at: new Date().toISOString()
                    };
                    
                    const { error } = await supabase
                      .from('currencies')
                      .update(updateData)
                      .eq('id', currency.id)
                      .eq('user_id', currency.user_id);
                      
                    if (error) {
                      console.error(`[useCurrenciesStore] Error updating currency ${currency.code} in database:`, error);
                    }
                  } catch (updateError) {
                    console.error(`[useCurrenciesStore] Error updating currency ${currency.code} in database:`, updateError);
                  }
                }));
                
                console.log('[useCurrenciesStore] Successfully updated user currencies with fresh rates');
              }
            } catch (updateError) {
              console.error('[useCurrenciesStore] Error updating currencies in database:', updateError);
              // Don't throw here - we already have the rates in memory which is the main goal
            }
          }
          
          return rates.rates;
        } catch (error) {
          console.error('[useCurrenciesStore] Error forcing update of exchange rates:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to force update exchange rates'
          });
          return null;
        }
      },

      convertAmount: (amount: number, fromCurrency: string, toCurrency: string) => {
        if (fromCurrency === toCurrency) return amount;
        
        const { exchangeRates } = get();
        if (!exchangeRates) return amount;

        try {
          // If rates are based on USD
          const fromRate = exchangeRates[fromCurrency] || 1;
          const toRate = exchangeRates[toCurrency] || 1;
          
          // Convert to base currency (USD) then to target currency
          return (amount / fromRate) * toRate;
        } catch (error) {
          console.error('Error converting currency:', error);
          return amount;
        }
      },

      initializeDefaultCurrencies: async (userId: string) => {
        if (!userId) {
          console.error('[useCurrenciesStore] No userId provided for initializeDefaultCurrencies');
          set({ error: 'No userId provided' });
          return;
        }

        try {
          console.log('[useCurrenciesStore] Starting default currencies initialization for user', userId);
          set({ loading: true, error: null });
          
          // First check if user already has any currencies to avoid duplicate key errors
          const { data: existingCurrencies, error: checkError } = await supabase
            .from('currencies')
            .select('code')
            .eq('user_id', userId);
            
          if (checkError) {
            console.error('[useCurrenciesStore] Error checking for existing currencies:', checkError);
            throw checkError;
          }
          
          // If user already has currencies, just fetch them and return
          if (existingCurrencies && existingCurrencies.length > 0) {
            console.log(`[useCurrenciesStore] User already has ${existingCurrencies.length} currencies, skipping initialization:`);
            console.log('[useCurrenciesStore] Existing currency codes:', existingCurrencies.map(c => c.code).join(', '));
            
            // Fetch existing currencies and update state
            await get().fetchCurrencies(userId);
            return;
          }
          
          console.log('[useCurrenciesStore] No existing currencies found, adding default currencies');
          
          // Prepare all currencies for batch insert
          const currenciesToInsert = defaultCurrencies.map(currency => ({
            id: generateUUID(),
            code: currency.code,
            name: currency.name,
            symbol: currency.symbol,
            is_default: currency.isDefault,
            rate: currency.rate,
            user_id: userId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }));
          
          // Insert all currencies in a single batch operation
          const { error: batchError } = await supabase
            .from('currencies')
            .insert(currenciesToInsert);
            
          if (batchError) {
            // If it's a duplicate key error, handle it gracefully
            if (batchError.code === '23505') {
              console.log('[useCurrenciesStore] Some currencies already exist (concurrent initialization)');
              // Just fetch the currencies that were already inserted
              await get().fetchCurrencies(userId);
              return;
            }
            throw batchError;
          }
          
          console.log('[useCurrenciesStore] Successfully initialized default currencies');
          
          // Fetch the newly added currencies
          await get().fetchCurrencies(userId);
        } catch (error) {
          console.error('[useCurrenciesStore] Error initializing default currencies:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to initialize default currencies',
            loading: false 
          });
          throw error; // Re-throw to allow error handling in calling code
        } finally {
          set({ loading: false });
        }
      },

      addCurrency: async (userId: string, currency: Omit<Currency, 'id'>) => {
        if (!userId) {
          console.error('No userId provided for addCurrency');
          set({ error: 'No userId provided' });
          return;
        }

        try {
          set({ loading: true, error: null });

          console.log('Adding/updating currency:', currency);

          // First try to find if this currency already exists for the user
          const { data: existingCurrency, error: findError } = await supabase
            .from('currencies')
            .select('id')
            .eq('code', currency.code)
            .eq('user_id', userId)
            .single();

          if (findError && findError.code !== 'PGRST116') { // PGRST116 is "no rows returned"
            console.error('Error checking for existing currency:', findError);
            throw findError;
          }

          let result;
          if (existingCurrency) {
            // Update existing currency
            console.log('Updating existing currency:', existingCurrency.id);
            
            // Prepare the data for update
            const currencyData = {
              code: currency.code,
              name: currency.name,
              symbol: currency.symbol,
              is_default: currency.isDefault || false,
              rate: currency.rate,
              user_id: userId,
              updated_at: new Date().toISOString()
            };
            
            result = await supabase
              .from('currencies')
              .update(currencyData)
              .eq('id', existingCurrency.id)
              .eq('user_id', userId)
              .select()
              .single();
          } else {
            // Insert new currency with generated UUID
            console.log('Creating new currency with generated ID');
            
            // Prepare the data for insert with ID
            const currencyData = {
              id: generateUUID(), // Generate UUID for the id field
              code: currency.code,
              name: currency.name,
              symbol: currency.symbol,
              is_default: currency.isDefault || false,
              rate: currency.rate || 1,
              user_id: userId,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            result = await supabase
              .from('currencies')
              .insert([currencyData])
              .select()
              .single();
          }

          if (result.error) {
            console.error('Error upserting currency:', result.error);
            throw result.error;
          }

          const newCurrency = result.data;

          // Update local state
          set(state => ({
            currencies: [
              ...state.currencies.filter(c => !(c.code === currency.code && c.user_id === userId)),
              {
                ...newCurrency,
                isDefault: newCurrency.is_default
              }
            ],
            loading: false,
            error: null
          }));

          // If this is marked as default, update other currencies to not be default
          if (currency.isDefault) {
            const { error: updateError } = await supabase
              .from('currencies')
              .update({ is_default: false })
              .eq('user_id', userId)
              .neq('id', newCurrency.id);

            if (updateError) {
              console.error('Error updating other currencies default status:', updateError);
            }

            // Update local state to reflect the default status change
            set(state => ({
              currencies: state.currencies.map(c => ({
                ...c,
                isDefault: c.id === newCurrency.id ? true : false
              }))
            }));
          }
        } catch (error) {
          console.error('Error upserting currency:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to add/update currency',
            loading: false 
          });
        }
      },

      updateCurrency: async (userId: string, currency: Currency) => {
        if (!userId) {
          console.error('No userId provided for updateCurrency');
          set({ error: 'No userId provided' });
          return;
        }

        try {
          set({ loading: true, error: null });
          
          // Create a clean update object with only the fields that exist in the database
          const updateData = {
            code: currency.code,
            name: currency.name,
            symbol: currency.symbol,
            is_default: currency.isDefault,
            rate: currency.rate,
            updated_at: new Date().toISOString()
          };

          console.log('Updating currency with data:', { 
            id: currency.id, 
            userId, 
            updateData 
          });

          const { error } = await supabase
            .from('currencies')
            .update(updateData)
            .eq('id', currency.id)
            .eq('user_id', userId);

          if (error) {
            console.error('Supabase update error:', error);
            throw error;
          }

          // Update local state with the camelCase version
          set(state => ({
            currencies: state.currencies.map(c => 
              c.id === currency.id ? { 
                ...c, 
                code: currency.code,
                name: currency.name,
                symbol: currency.symbol,
                isDefault: currency.isDefault,
                rate: currency.rate,
                updated_at: updateData.updated_at
              } : c
            ),
            loading: false,
            error: null
          }));
        } catch (error) {
          console.error('Error updating currency:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to update currency',
            loading: false 
          });
        }
      },

      deleteCurrency: async (userId: string, currencyId: string) => {
        if (!userId) {
          console.error('No userId provided for deleteCurrency');
          set({ error: 'No userId provided' });
          return;
        }

        try {
          set({ loading: true, error: null });
          
          // Check if this is the default currency
          const currencyToDelete = get().currencies.find(c => c.id === currencyId);
          if (currencyToDelete?.isDefault) {
            set({ 
              error: 'Cannot delete the default currency',
              loading: false 
            });
            return;
          }

          const { error } = await supabase
            .from('currencies')
            .delete()
            .eq('id', currencyId)
            .eq('user_id', userId);

          if (error) throw error;

          set(state => ({
            currencies: state.currencies.filter(c => c.id !== currencyId),
            loading: false,
            error: null
          }));
        } catch (error) {
          console.error('Error deleting currency:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to delete currency',
            loading: false 
          });
        }
      },

      setDefaultCurrency: async (userId: string, currencyId: string) => {
        if (!userId) {
          console.error('No userId provided for setDefaultCurrency');
          set({ error: 'No userId provided' });
          return;
        }

        try {
          set({ loading: true, error: null });
          
          // First, unset all default currencies
          const { error: updateError } = await supabase
            .from('currencies')
            .update({ is_default: false, updated_at: new Date().toISOString() })
            .eq('user_id', userId);

          if (updateError) throw updateError;

          // Then set the new default currency
          const { error } = await supabase
            .from('currencies')
            .update({ is_default: true, updated_at: new Date().toISOString() })
            .eq('id', currencyId)
            .eq('user_id', userId);

          if (error) throw error;

          // Update local state
          set(state => ({
            currencies: state.currencies.map(c => ({
              ...c,
              isDefault: c.id === currencyId
            })),
            loading: false,
            error: null
          }));
        } catch (error) {
          console.error('Error setting default currency:', error);
          set({ 
            error: error instanceof Error ? error.message : 'Failed to set default currency',
            loading: false 
          });
        }
      }
    }),
    {
      name: 'currencies-storage',
      storage: createJSONStorage(() => getStorage())
    }
  )
); 